import { useEffect, useId, useRef, type ReactNode } from 'react';

interface ModalProps {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  side?: boolean;
  wide?: boolean;
  actions?: ReactNode;
  error?: string | null;
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Modal accesible: foco inicial, trampa de Tab, Escape, devolución del foco y aria-modal. */
export function Modal({ title, subtitle, children, onClose, side, wide, actions, error }: ModalProps) {
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const first = node?.querySelector<HTMLElement>('[data-autofocus]') ?? node?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && node) {
        const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const firstEl = items[0]!;
        const lastEl = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previous?.focus?.();
    };
  }, [onClose]);

  return (
    <div className={`modalback${side ? ' side' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section ref={ref} className={`modal${side ? ' side' : ''}${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="panelhead">
          <div>
            <h2 id={titleId}>{title}</h2>
            {subtitle ? <div className="muted">{subtitle}</div> : null}
          </div>
          <button type="button" className="ghost" aria-label="Cerrar" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modalbody">{children}</div>
        {error ? (
          <div className="error" role="alert">
            {error}
          </div>
        ) : null}
        {actions !== undefined ? (
          <div className="actions">
            <button type="button" className="button" onClick={onClose}>
              Cancelar
            </button>
            {actions}
          </div>
        ) : null}
      </section>
    </div>
  );
}
